import { useState, useEffect, useCallback } from 'react';

import {
  useNotificationAlarmsQuery,
  useUpdateNotificationAlarmsMutation,
} from '@features/notification/hooks/useNotificationAlarms';
import Toggle from '@shared/components/atoms/Toggle';
import Button from '@shared/components/atoms/Button';
import Alert from '@shared/components/atoms/Alert';
import Confirm from '@shared/components/atoms/Confirm';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import { MobileLayout } from '@shared/components/organisms/mobile';
import { useAlert, useConfirm, useIsMobile } from '@shared/hooks';

import styles from './NotificationSettingPage.module.scss';

/**
 * NotificationSettingPage 컴포넌트
 * - 알림 설정 페이지
 * - 모바일/PC 모두 지원
 */
function NotificationSettingPage() {
  const isMobile = useIsMobile();
  const { alertState, showAlert, closeAlert } = useAlert();
  const { confirmState, showConfirm, closeConfirm } = useConfirm();

  // 알림 설정 조회
  const { data: alarmsData, isLoading } = useNotificationAlarmsQuery();

  // 로컬 상태로 토글 값 관리
  const [appPushEnabled, setAppPushEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  // API 응답 데이터를 로컬 상태로 동기화
  useEffect(
    function () {
      if (alarmsData?.resultData) {
        const { app, email } = alarmsData.resultData;
        setAppPushEnabled(app ?? false);
        setEmailEnabled(email ?? false);
      }
    },
    [alarmsData],
  );

  // 알림 설정 업데이트 Mutation
  const updateMutation = useUpdateNotificationAlarmsMutation({
    onSuccess: function () {
      showAlert('알림 설정이 저장되었습니다.');
    },
    onError: function () {
      showAlert('알림 설정 저장에 실패했습니다.');
    },
  });

  /**
   * 확인 버튼 클릭 핸들러
   * - 현재 토글 상태를 서버에 저장
   */
  const handleConfirm = useCallback(
    function () {
      updateMutation.mutate({
        app: appPushEnabled,
        email: emailEnabled,
      });
    },
    [appPushEnabled, emailEnabled, updateMutation],
  );

  // 모바일 레이아웃 렌더링
  if (isMobile) {
    return (
      <>
        <MobileLayout title="알림 설정">
          <div className={styles.mobileContainer}>
            {/* 결재 알림 설정 섹션 */}
            <div className={styles.section}>
              <p className={styles.description}>
                · 결재와 관련된 새로운 소식이 있을 경우 알림이 발송됩니다.
              </p>

              {/* 휴대폰 알림 설정 */}
              <div className={styles.settingItem}>
                <span className={styles.settingLabel}>휴대폰 알림</span>
                {isLoading ? (
                  <div className={styles.loadingText}>로딩 중...</div>
                ) : (
                  <Toggle
                    checked={appPushEnabled}
                    onChange={(e) => setAppPushEnabled(e.target.checked)}
                  />
                )}
              </div>

              {/* 이메일 알림 설정 */}
              <div className={styles.settingItem}>
                <span className={styles.settingLabel}>이메일 알림</span>
                {isLoading ? (
                  <div className={styles.loadingText}>로딩 중...</div>
                ) : (
                  <Toggle
                    checked={emailEnabled}
                    onChange={(e) => setEmailEnabled(e.target.checked)}
                  />
                )}
              </div>
            </div>

            {/* 확인 버튼 */}
            <div className={styles.mobileButtonContainer}>
              <Button
                variant="primary"
                size="large"
                className={styles.confirmButton}
                onClick={handleConfirm}
                disabled={isLoading || updateMutation.isPending}
              >
                저장하기
              </Button>
            </div>
          </div>
        </MobileLayout>

        {/* Alert 컴포넌트 */}
        <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
      </>
    );
  }

  // PC 레이아웃 렌더링
  return (
    <MainLayout>
      <ContentCard className={styles.contentCardMarginFix}>
        <div className={styles.notificationSettingPage}>
          {/* 결재 알림 설정 섹션 */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>결재 알림 설정</h2>
            </div>
            <div className={styles.divider} />

            {/* 앱 Push 알림 설정 */}
            <div className={styles.settingItem}>
              <span className={styles.settingLabel}>앱 Push 알림 설정</span>
              {isLoading ? (
                <div className={styles.loadingText}>로딩 중...</div>
              ) : (
                <Toggle
                  checked={appPushEnabled}
                  onChange={(e) => setAppPushEnabled(e.target.checked)}
                />
              )}
            </div>

            {/* 이메일 알림 설정 */}
            <div className={styles.settingItem}>
              <span className={styles.settingLabel}>이메일 알림 설정</span>
              {isLoading ? (
                <div className={styles.loadingText}>로딩 중...</div>
              ) : (
                <Toggle
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                />
              )}
            </div>

            {/* 저장 버튼 */}
            <div className={styles.buttonContainer}>
              <Button
                variant="primary"
                className={styles.saveButton}
                onClick={handleConfirm}
                disabled={isLoading || updateMutation.isPending}
              >
                {updateMutation.isPending ? '저장 중...' : '저장하기'}
              </Button>
            </div>
          </div>
        </div>
      </ContentCard>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </MainLayout>
  );
}

export default NotificationSettingPage;
